/**
 * Consultas e gravações de compras pelo TanStack Query.
 *
 * Papel: aqui ficam `usePedidosCompra`, `useClassificacaoFinanceira` e
 * `useFotoDoItem`, que mantêm em cache o que a tela lê, e as duas mutações que
 * gravam e mandam a listagem se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_COMPRAS`. A foto tem
 * chave própria por item, e `enabled` desligado até alguém abri-la — sem isso,
 * montar o quadro dispararia uma requisição de imagem por cartão.
 *
 * `staleTime` da foto é longo porque a imagem de um item não muda sozinha: uma
 * vez carregada, reabrir a mesma foto não custa nova requisição.
 */
import { type NovoPedidoCompra } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CHAVE_FINANCAS } from '@/modules/financas';

import {
  atualizarPedido,
  buscarClassificacaoFinanceira,
  buscarFotoDoItem,
  buscarPedidos,
  criarPedido,
  excluirPedido,
} from '../api/compras';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_COMPRAS = ['compras'] as const;

/** Chave da lista de pedidos. */
export const CHAVE_PEDIDOS = [...CHAVE_COMPRAS, 'pedidos'] as const;

/** Chave das categorias e dos favorecidos que o cartão financeiro oferece. */
export const CHAVE_CLASSIFICACAO = [...CHAVE_COMPRAS, 'classificacao-financeira'] as const;

/** Quanto tempo a imagem de um item permanece fresca no cache. */
const VALIDADE_DA_FOTO_EM_MS = 30 * 60 * 1000;

/**
 * Lê os pedidos de compra.
 *
 * @param habilitado - `false` desliga a consulta, para quem não tem o módulo.
 * @returns Resultado da consulta, do pedido mais recente para o mais antigo.
 */
export function usePedidosCompra(habilitado = true) {
  return useQuery({
    queryKey: CHAVE_PEDIDOS,
    queryFn: ({ signal }) => buscarPedidos(signal),
    enabled: habilitado,
  });
}

/**
 * Lê as categorias de despesa e os favorecidos do caixa, para os selects e o
 * autopreenchimento do cartão financeiro de cada item.
 *
 * @returns Resultado da consulta; enquanto ela não responde, o formulário
 *   oferece só "— Selecionar —" e não preenche nada pelo fornecedor.
 */
export function useClassificacaoFinanceira() {
  return useQuery({
    queryKey: CHAVE_CLASSIFICACAO,
    queryFn: ({ signal }) => buscarClassificacaoFinanceira(signal),
  });
}

/**
 * Lê a imagem de um item.
 *
 * @param itemId - O item cuja imagem se quer, ou `null` para não pedir nenhuma.
 * @returns Resultado da consulta, com `foto` nula quando o item não tem imagem.
 */
export function useFotoDoItem(itemId: string | null) {
  return useQuery({
    queryKey: [...CHAVE_COMPRAS, 'foto', itemId],
    queryFn: ({ signal }) => buscarFotoDoItem(itemId ?? '', signal),
    enabled: itemId !== null,
    staleTime: VALIDADE_DA_FOTO_EM_MS,
  });
}

/** Variáveis da mutação que cria ou altera conforme venha identificador. */
interface SalvarPedidoVariaveis {
  /** Identificador do pedido a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: NovoPedidoCompra;
  /**
   * O que fazer com as despesas do pedido ao tirá-lo de `comprado`.
   *
   * Só tem efeito nesse caminho, e omiti-lo mantém as despesas. Quem retrocede
   * um pedido lançado precisa perguntar antes e transmitir a resposta aqui —
   * apagar por conta própria tiraria do caixa uma despesa que alguém pode já ter
   * conciliado.
   */
  lancamentos?: 'excluir' | 'manter';
}

/**
 * Cria ou altera um pedido, conforme venha identificador.
 *
 * @returns A mutação, que recebe `id` e os dados.
 */
export function useSalvarPedido() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados, lancamentos }: SalvarPedidoVariaveis) =>
      id === null ? criarPedido(dados) : atualizarPedido(id, dados, lancamentos),
    // Invalida também o caixa: a gravação que leva o pedido a `comprado` grava
    // despesas em `financas_lancamentos`, e a tela de finanças as mostraria com
    // atraso se o cache dela continuasse válido. A chave vem do `index.ts`
    // daquele módulo, e não escrita à mão: uma cópia literal deixaria de casar
    // no dia em que o prefixo de lá mudasse, sem nada acusar. A mesma gravação
    // cria o favorecido que faltava, e é por isso que a classificação também
    // volta a ser buscada.
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_PEDIDOS });
      await clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_CLASSIFICACAO });
      await clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_FINANCAS });
    },
  });
}

/**
 * Apaga um pedido.
 *
 * Invalida o módulo inteiro, e não só a lista: as fotos dos itens apagados
 * continuariam em cache, e reabrir um item recriado na mesma posição mostraria a
 * imagem do anterior.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirPedido() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => excluirPedido(id),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_COMPRAS }),
  });
}
